/**
 * الأوامر التجارية المحلية: إعدادات المنصة، الباقات، الاشتراكات، المدفوعات،
 * دعوات مسؤول المنشأة، والتسجيل الذاتي.
 *
 * القواعد الحاكمة:
 * - لا يعدّل أي أمر هنا `companies.status` بسبب اشتراك أو دفعة.
 * - سجل الدفع لا يغيّر الاشتراك تلقائيًا.
 * - دعوة المسؤول تُخزَّن كتجزئة فقط، ولا تُنشئ عضوية قبل قبول صاحب البريد.
 * - إعادة إرسال طلب التسجيل بنفس requestId تعيد نفس المنشأة ولا تنشئ أخرى.
 */

import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import {
  DEFAULT_PLATFORM_OPERATIONS_SETTINGS,
  isPaymentStatus,
  isSubscriptionStatus,
  type CompanyInvite,
  type CompanySubscription,
  type PaymentStatus,
  type PlatformOperationsSettings,
  type RegistrationRequest,
  type SubscriptionEvent,
  type SubscriptionPayment,
  type SubscriptionPlan,
  type SubscriptionSource,
  type SubscriptionStatus,
} from '@/domain/billing';
import type { Company, Membership, Role } from '@/domain/types';
import { sha256Hex } from '../blob-store';
import type {
  AcceptCompanyInviteResult,
  CreateCompanyInviteInput,
  CreateCompanyInviteResult,
  RecordSubscriptionPaymentInput,
  SaveCompanySubscriptionInput,
  SaveSubscriptionPlanInput,
  SelfRegistrationInput,
  UpdateSubscriptionPaymentInput,
} from '../types';
import type { LocalCore } from './commands';
import { seedCompanyStructure } from './company-setup';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CURRENCY_PATTERN = /^[A-Z]{3,8}$/;
const SLUG_PATTERN = /^[a-z0-9-]{2,40}$/;
const PLAN_CODE_PATTERN = /^[a-z0-9-]{2,40}$/;
const INVITE_TOKEN_PREFIX = 'tf_inv_';

function requireUser(core: LocalCore): string {
  const userId = core.policy().userId;
  if (!userId) throw AppError.unauthenticated();
  return userId;
}

function assertPlatformOwner(core: LocalCore, message: string): string {
  const userId = requireUser(core);
  if (!core.policy().isPlatformOwner) throw AppError.forbidden(message);
  return userId;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function requireEmail(email: string): string {
  const normalized = normalizeEmail(email);
  if (!EMAIL_PATTERN.test(normalized)) {
    throw AppError.validation('البريد الإلكتروني غير صالح');
  }
  return normalized;
}

function requireCurrency(currency: string): string {
  const normalized = currency.trim().toUpperCase();
  if (!CURRENCY_PATTERN.test(normalized)) {
    throw AppError.validation('رمز العملة غير صالح');
  }
  return normalized;
}

function requireAmount(amountCents: number): number {
  if (!Number.isSafeInteger(amountCents) || amountCents < 0) {
    throw AppError.validation('المبلغ غير صالح');
  }
  return amountCents;
}

function requireText(value: string, label: string, min: number, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length < min || trimmed.length > max) {
    throw AppError.validation(`${label} يجب أن يكون بين ${min} و${max} حرفًا`);
  }
  return trimmed;
}

function optionalText(value: string | null | undefined, max = 2000): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.length > max) throw AppError.validation(`النص أطول من ${max} حرف`);
  return trimmed;
}

function validDate(value: string | null | undefined, label: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) throw AppError.validation(`تاريخ ${label} غير صالح`);
  return date.toISOString();
}

export async function readPlatformOperationsSettings(
  core: LocalCore,
): Promise<PlatformOperationsSettings> {
  const row = await core.rawGet('platform_settings', 'platform');
  return row ?? { ...DEFAULT_PLATFORM_OPERATIONS_SETTINGS };
}

export async function getPlatformOperationsSettingsLocal(
  core: LocalCore,
): Promise<PlatformOperationsSettings> {
  assertPlatformOwner(core, 'إعدادات المنصة متاحة لمالك المنصة فقط');
  return readPlatformOperationsSettings(core);
}

export async function setPlatformOperationsSettingsLocal(
  core: LocalCore,
  input: Omit<PlatformOperationsSettings, 'id' | 'updatedAt' | 'updatedBy'>,
): Promise<PlatformOperationsSettings> {
  const userId = assertPlatformOwner(core, 'تعديل إعدادات المنصة متاح لمالك المنصة فقط');
  if (!Number.isInteger(input.defaultTrialDays) || input.defaultTrialDays < 0 || input.defaultTrialDays > 365) {
    throw AppError.validation('مدة التجربة يجب أن تكون بين 0 و365 يومًا');
  }
  if (input.defaultPlanId) {
    const plan = await core.rawGet('subscription_plans', input.defaultPlanId);
    if (!plan || !plan.isActive) {
      throw AppError.validation('الباقة الافتراضية غير موجودة أو غير مفعّلة');
    }
  }
  const before = await core.rawGet('platform_settings', 'platform');
  const settings: PlatformOperationsSettings = {
    id: 'platform',
    selfRegistrationEnabled: input.selfRegistrationEnabled,
    subscriptionsEnabled: input.subscriptionsEnabled,
    paymentsEnabled: input.paymentsEnabled,
    defaultTrialDays: input.defaultTrialDays,
    defaultPlanId: input.defaultPlanId ?? null,
    updatedAt: core.now().toISOString(),
    updatedBy: userId,
  };
  await core.rawPut('platform_settings', settings);
  await core.audit({
    companyId: null,
    action: 'platform.operations_settings_updated',
    entityType: 'platform_settings',
    entityId: 'platform',
    before,
    after: {
      selfRegistrationEnabled: settings.selfRegistrationEnabled,
      subscriptionsEnabled: settings.subscriptionsEnabled,
      paymentsEnabled: settings.paymentsEnabled,
      defaultTrialDays: settings.defaultTrialDays,
      defaultPlanId: settings.defaultPlanId,
    },
  });
  return settings;
}

export async function saveSubscriptionPlanLocal(
  core: LocalCore,
  input: SaveSubscriptionPlanInput,
): Promise<SubscriptionPlan> {
  assertPlatformOwner(core, 'إدارة الباقات متاحة لمالك المنصة فقط');
  const code = input.code.trim().toLowerCase();
  if (!PLAN_CODE_PATTERN.test(code)) {
    throw AppError.validation('رمز الباقة يجب أن يكون أحرفًا إنجليزية صغيرة وأرقامًا وشرطات');
  }
  const name = requireText(input.name, 'اسم الباقة', 2, 120);
  const description = optionalText(input.description, 500);
  const currency = requireCurrency(input.currency);
  if (!Number.isInteger(input.trialDays) || input.trialDays < 0 || input.trialDays > 365) {
    throw AppError.validation('مدة التجربة يجب أن تكون بين 0 و365 يومًا');
  }
  if (!['monthly', 'yearly', 'custom'].includes(input.billingInterval)) {
    throw AppError.validation('دورة الفوترة غير صالحة');
  }
  const now = core.now().toISOString();
  const existing = input.planId ? await core.rawGet('subscription_plans', input.planId) : null;
  if (input.planId && !existing) throw AppError.notFound('الباقة غير موجودة');

  const all = await core.rawAll('subscription_plans');
  if (all.some((plan) => plan.code.toLowerCase() === code && plan.id !== existing?.id)) {
    throw AppError.conflict('رمز الباقة مستخدم مسبقًا');
  }

  const plan: SubscriptionPlan = {
    id: existing?.id ?? newId(),
    code,
    name,
    description,
    priceCents: requireAmount(input.priceCents),
    currency,
    billingInterval: input.billingInterval,
    trialDays: input.trialDays,
    features: input.features ?? {},
    isActive: Boolean(input.isActive),
    sortOrder: Number.isSafeInteger(input.sortOrder) ? input.sortOrder : 0,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  await core.rawPut('subscription_plans', plan);

  if (!plan.isActive) {
    const settings = await readPlatformOperationsSettings(core);
    if (settings.defaultPlanId === plan.id) {
      await core.rawPut('platform_settings', {
        ...settings,
        defaultPlanId: null,
        updatedAt: now,
      });
    }
  }

  await core.audit({
    companyId: null,
    action: 'billing.plan_saved',
    entityType: 'subscription_plan',
    entityId: plan.id,
    before: existing,
    after: { code: plan.code, name: plan.name, isActive: plan.isActive },
  });
  return plan;
}

export async function saveCompanySubscriptionLocal(
  core: LocalCore,
  input: SaveCompanySubscriptionInput,
): Promise<CompanySubscription> {
  assertPlatformOwner(core, 'إدارة الاشتراكات متاحة لمالك المنصة فقط');
  if (!isSubscriptionStatus(input.status)) {
    throw AppError.validation('حالة الاشتراك غير صالحة');
  }
  if (!['manual', 'self_service'].includes(input.source)) {
    throw AppError.validation('مصدر الاشتراك غير صالح');
  }
  const company = await core.rawGet('companies', input.companyId);
  if (!company || company.deletedAt) throw AppError.notFound('المنشأة غير موجودة أو محذوفة');
  const plan = input.planId ? await core.rawGet('subscription_plans', input.planId) : null;
  if (input.planId && !plan) throw AppError.validation('الباقة غير موجودة');

  const now = core.now().toISOString();
  const startsAt = validDate(input.startsAt, 'البداية') ?? now;
  let endsAt = validDate(input.endsAt, 'النهاية');
  let trialEndsAt = validDate(input.trialEndsAt, 'نهاية التجربة');
  if (input.status === 'trialing' && !trialEndsAt) {
    const settings = await readPlatformOperationsSettings(core);
    const days = plan?.trialDays || settings.defaultTrialDays || 14;
    trialEndsAt = new Date(new Date(startsAt).getTime() + days * 86_400_000).toISOString();
  }
  if (endsAt && new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
    throw AppError.validation('تاريخ نهاية الاشتراك يجب أن يكون بعد بدايته');
  }
  if (!endsAt) endsAt = null;

  const existing = (await core.rawFind('company_subscriptions', 'companyId', company.id))[0] ?? null;
  const subscription: CompanySubscription = {
    id: existing?.id ?? newId(),
    companyId: company.id,
    planId: input.planId ?? null,
    status: input.status,
    startsAt,
    endsAt,
    trialEndsAt,
    cancelAtPeriodEnd: Boolean(input.cancelAtPeriodEnd),
    source: input.source as SubscriptionSource,
    notes: optionalText(input.notes),
    createdBy: existing?.createdBy ?? core.policy().userId ?? null,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  await core.rawPut('company_subscriptions', subscription);

  const event: SubscriptionEvent = {
    id: newId(),
    subscriptionId: subscription.id,
    companyId: subscription.companyId,
    action: existing
      ? existing.status !== subscription.status
        ? 'status_changed'
        : 'updated'
      : 'created',
    fromStatus: existing?.status ?? null,
    toStatus: subscription.status,
    metadata: {
      planId: subscription.planId,
      source: subscription.source,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
    },
    actorUserId: core.policy().userId ?? null,
    createdAt: now,
  };
  await core.rawPut('subscription_events', event);
  await core.audit({
    companyId: company.id,
    action: 'billing.subscription_saved',
    entityType: 'company_subscription',
    entityId: subscription.id,
    before: existing,
    after: {
      status: subscription.status,
      planId: subscription.planId,
      startsAt: subscription.startsAt,
      endsAt: subscription.endsAt,
      trialEndsAt: subscription.trialEndsAt,
    },
  });
  return subscription;
}

function assertPaymentsEnabled(settings: PlatformOperationsSettings): void {
  if (!settings.paymentsEnabled) {
    throw AppError.validation('تحصيل المدفوعات معطّل من إعدادات المنصة');
  }
}

export async function recordSubscriptionPaymentLocal(
  core: LocalCore,
  input: RecordSubscriptionPaymentInput,
): Promise<SubscriptionPayment> {
  assertPlatformOwner(core, 'إدارة المدفوعات متاحة لمالك المنصة فقط');
  const settings = await readPlatformOperationsSettings(core);
  assertPaymentsEnabled(settings);
  if (!isPaymentStatus(input.status)) throw AppError.validation('حالة الدفع غير صالحة');
  const amountCents = requireAmount(input.amountCents);
  const currency = requireCurrency(input.currency);
  const company = await core.rawGet('companies', input.companyId);
  if (!company || company.deletedAt) throw AppError.notFound('المنشأة غير موجودة أو محذوفة');
  if (input.subscriptionId) {
    const subscription = await core.rawGet('company_subscriptions', input.subscriptionId);
    if (!subscription || subscription.companyId !== company.id) {
      throw AppError.validation('الاشتراك لا يتبع هذه المنشأة');
    }
  }

  const clientRef = optionalText(input.clientRef, 120);
  if (clientRef) {
    const existing = (await core.rawFind('subscription_payments', 'companyId', company.id)).find(
      (payment) => payment.clientRef === clientRef,
    );
    if (existing) return existing;
  }

  const now = core.now().toISOString();
  const payment: SubscriptionPayment = {
    id: newId(),
    companyId: company.id,
    subscriptionId: input.subscriptionId ?? null,
    status: input.status as PaymentStatus,
    amountCents,
    currency,
    dueAt: validDate(input.dueAt, 'الاستحقاق'),
    paidAt: validDate(input.paidAt, 'الدفع'),
    provider: optionalText(input.provider, 80),
    providerRef: optionalText(input.providerRef, 160),
    clientRef,
    notes: optionalText(input.notes),
    createdBy: core.policy().userId ?? null,
    createdAt: now,
    updatedAt: now,
  };
  await core.rawPut('subscription_payments', payment);

  if (payment.subscriptionId) {
    const subscription = await core.rawGet('company_subscriptions', payment.subscriptionId);
    await core.rawPut('subscription_events', {
      id: newId(),
      subscriptionId: payment.subscriptionId,
      companyId: company.id,
      action: 'payment_recorded',
      fromStatus: subscription?.status ?? null,
      toStatus: subscription?.status ?? null,
      metadata: {
        paymentId: payment.id,
        paymentStatus: payment.status,
        amountCents: payment.amountCents,
        currency: payment.currency,
      },
      actorUserId: core.policy().userId ?? null,
      createdAt: now,
    } satisfies SubscriptionEvent);
  }
  await core.audit({
    companyId: company.id,
    action: 'billing.payment_recorded',
    entityType: 'subscription_payment',
    entityId: payment.id,
    after: {
      status: payment.status,
      amountCents: payment.amountCents,
      currency: payment.currency,
      subscriptionId: payment.subscriptionId,
    },
  });
  return payment;
}

export async function updateSubscriptionPaymentLocal(
  core: LocalCore,
  input: UpdateSubscriptionPaymentInput,
): Promise<SubscriptionPayment> {
  assertPlatformOwner(core, 'إدارة المدفوعات متاحة لمالك المنصة فقط');
  const settings = await readPlatformOperationsSettings(core);
  assertPaymentsEnabled(settings);
  if (!isPaymentStatus(input.status)) throw AppError.validation('حالة الدفع غير صالحة');
  const existing = await core.rawGet('subscription_payments', input.paymentId);
  if (!existing) throw AppError.notFound('سجل الدفع غير موجود');
  const payment: SubscriptionPayment = {
    ...existing,
    status: input.status as PaymentStatus,
    paidAt: validDate(input.paidAt, 'الدفع'),
    notes: optionalText(input.notes),
    updatedAt: core.now().toISOString(),
  };
  await core.rawPut('subscription_payments', payment);

  if (payment.subscriptionId) {
    const subscription = await core.rawGet('company_subscriptions', payment.subscriptionId);
    await core.rawPut('subscription_events', {
      id: newId(),
      subscriptionId: payment.subscriptionId,
      companyId: payment.companyId,
      action: 'payment_updated',
      fromStatus: subscription?.status ?? null,
      toStatus: subscription?.status ?? null,
      metadata: {
        paymentId: payment.id,
        paymentStatus: payment.status,
        previousStatus: existing.status,
      },
      actorUserId: core.policy().userId ?? null,
      createdAt: payment.updatedAt,
    } satisfies SubscriptionEvent);
  }
  await core.audit({
    companyId: payment.companyId,
    action: 'billing.payment_updated',
    entityType: 'subscription_payment',
    entityId: payment.id,
    before: { status: existing.status },
    after: { status: payment.status, paidAt: payment.paidAt },
  });
  return payment;
}

async function hashToken(token: string): Promise<string> {
  const bytes = new TextEncoder().encode(token);
  const copy = new Uint8Array(bytes);
  return sha256Hex(copy.buffer);
}

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return `${INVITE_TOKEN_PREFIX}${[...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

export async function createCompanyInviteLocal(
  core: LocalCore,
  input: CreateCompanyInviteInput,
): Promise<CreateCompanyInviteResult> {
  const userId = assertPlatformOwner(core, 'دعوة مسؤول المنشأة متاحة لمالك المنصة فقط');
  const email = requireEmail(input.email);
  const expiresHours = input.expiresHours ?? 168;
  if (!Number.isInteger(expiresHours) || expiresHours < 1 || expiresHours > 720) {
    throw AppError.validation('صلاحية الدعوة يجب أن تكون بين ساعة و720 ساعة');
  }
  const company = await core.rawGet('companies', input.companyId);
  if (!company || company.deletedAt) throw AppError.notFound('المنشأة غير موجودة أو محذوفة');
  if (company.status !== 'active') {
    throw AppError.validation('لا يمكن دعوة مسؤول إلى منشأة غير نشطة');
  }

  let role: Role | null = null;
  if (input.roleId) {
    role = await core.rawGet('roles', input.roleId);
    if (!role || role.companyId !== company.id || role.deletedAt || role.isPlatformRole) {
      throw AppError.validation('دور المسؤول غير صالح لهذه المنشأة');
    }
  } else {
    const roles = await core.rawFind('roles', 'companyId', company.id);
    role = roles.find((candidate) => candidate.key === 'company_admin' && !candidate.deletedAt) ?? null;
    if (!role) throw AppError.validation('دور مسؤول المنشأة غير موجود');
  }

  const now = core.now().toISOString();
  const pending = await core.rawFind('company_invites', 'companyId', company.id);
  for (const invite of pending) {
    if (invite.status === 'pending' && invite.email === email) {
      await core.rawPut('company_invites', { ...invite, status: 'revoked' });
    }
  }

  const token = randomToken();
  const expiresAt = new Date(core.now().getTime() + expiresHours * 3_600_000).toISOString();
  const invite: CompanyInvite = {
    id: newId(),
    companyId: company.id,
    email,
    roleId: role.id,
    tokenHash: await hashToken(token),
    status: 'pending',
    expiresAt,
    createdBy: userId,
    acceptedBy: null,
    acceptedAt: null,
    createdAt: now,
  };
  await core.rawPut('company_invites', invite);
  await core.audit({
    companyId: company.id,
    action: 'company.invite_created',
    entityType: 'company_invite',
    entityId: invite.id,
    after: { roleId: role.id, roleKey: role.key, expiresAt },
  });
  return { inviteId: invite.id, token, expiresAt };
}

export async function acceptCompanyInviteLocal(
  core: LocalCore,
  input: { token: string },
): Promise<AcceptCompanyInviteResult> {
  // قد يصل المستخدم من رابط الدعوة مباشرة بعد تسجيل الدخول أو إنشاء الحساب،
  // لذا نُحدّث سياق الجلسة قبل قراءة الهوية أو العضوية.
  await core.refresh();
  const userId = requireUser(core);
  const token = input.token.trim();
  if (token.length < 16) throw AppError.validation('رابط الدعوة غير صالح');
  const tokenHash = await hashToken(token);
  const invites = await core.rawAll('company_invites');
  const invite = invites.find((candidate) => candidate.tokenHash === tokenHash) ?? null;
  if (!invite) throw AppError.validation('رابط الدعوة غير صالح أو منتهي');

  const company = await core.rawGet('companies', invite.companyId);
  const role = await core.rawGet('roles', invite.roleId);
  const now = core.now().toISOString();

  if (invite.status === 'accepted') {
    if (invite.acceptedBy === userId) {
      const memberships = await core.rawFind('memberships', 'companyId', invite.companyId);
      const membership = memberships.find((candidate) => candidate.userId === userId);
      if (membership && company && role) {
        return {
          membershipId: membership.id,
          companyId: company.id,
          companyName: company.name,
          roleKey: role.key,
        };
      }
    }
    throw AppError.conflict('تم استخدام رابط الدعوة مسبقًا');
  }
  if (invite.status !== 'pending') throw AppError.validation('رابط الدعوة ملغى أو غير صالح');
  if (new Date(invite.expiresAt).getTime() <= core.now().getTime()) {
    await core.rawPut('company_invites', { ...invite, status: 'expired' });
    throw AppError.validation('انتهت صلاحية رابط الدعوة. اطلب دعوة جديدة.');
  }
  if (!company || company.deletedAt || company.status !== 'active') {
    throw AppError.validation('المنشأة غير متاحة لقبول الدعوة حاليًا');
  }
  if (!role || role.deletedAt || role.isPlatformRole || role.companyId !== company.id) {
    throw AppError.validation('الدور المرتبط بالدعوة لم يعد صالحًا');
  }

  const profile = await core.rawGet('profiles', userId);
  const email = profile?.email?.trim().toLowerCase() ?? '';
  if (!email || email !== invite.email) {
    throw AppError.forbidden('رابط الدعوة مخصص لبريد إلكتروني آخر. سجّل الدخول بالبريد المدعو.');
  }

  const existing = (await core.rawFind('memberships', 'companyId', company.id)).find(
    (candidate) => candidate.userId === userId,
  );
  let membership: Membership;
  if (existing) {
    membership = {
      ...existing,
      status: 'active',
      jobTitle: existing.jobTitle ?? role.name,
      isCompanyOwner: existing.isCompanyOwner || role.key === 'company_admin',
      joinedAt: existing.joinedAt ?? now,
      endedAt: null,
      deletedAt: null,
      deletedBy: null,
      deletedReason: null,
      deletedScopeType: null,
      deletedScopeId: null,
      updatedAt: now,
    };
  } else {
    membership = {
      id: newId(),
      companyId: company.id,
      userId,
      employeeNo: null,
      jobTitle: role.name,
      status: 'active',
      isCompanyOwner: role.key === 'company_admin',
      primaryBranchId: null,
      workingDays: [0, 1, 2, 3, 4, 6],
      joinedAt: now,
      endedAt: null,
      createdAt: now,
      updatedAt: now,
    };
  }
  await core.rawPut('memberships', membership);
  const memberProfile = await core.rawGet('membership_profiles', membership.id);
  if (!memberProfile) {
    const accountProfile = await core.rawGet('profiles', userId);
    await core.rawPut('membership_profiles', {
      id: membership.id,
      membershipId: membership.id,
      userId,
      companyId: company.id,
      fullName: accountProfile?.fullName ?? 'عضو المنشأة',
      phone: null,
      avatarDataUrl: null,
      createdAt: now,
      updatedAt: now,
    });
  }

  const membershipRoles = await core.rawFind('membership_roles', 'membershipId', membership.id);
  if (!membershipRoles.some((item) => item.roleId === role.id)) {
    await core.rawPut('membership_roles', {
      id: newId(),
      membershipId: membership.id,
      roleId: role.id,
      assignedBy: userId,
      assignedAt: now,
      expiresAt: null,
    });
  }

  await core.rawPut('company_invites', {
    ...invite,
    status: 'accepted',
    acceptedBy: userId,
    acceptedAt: now,
  });
  await core.rawPut('notifications', {
    id: newId(),
    companyId: company.id,
    recipientUserId: userId,
    type: 'membership.added',
    title: 'تم ربطك بالمنشأة',
    body: company.name,
    taskId: null,
    payload: { membershipId: membership.id },
    readAt: null,
    createdAt: now,
  });
  await core.audit({
    companyId: company.id,
    action: 'company.invite_accepted',
    entityType: 'membership',
    entityId: membership.id,
    after: { inviteId: invite.id, roleId: role.id, roleKey: role.key },
  });
  await core.refresh();
  return {
    membershipId: membership.id,
    companyId: company.id,
    companyName: company.name,
    roleKey: role.key,
  };
}

export async function selfRegisterCompanyLocal(
  core: LocalCore,
  input: SelfRegistrationInput,
): Promise<Company> {
  // التسجيل الذاتي قد يبدأ فور إنشاء الحساب وقبل أن تُكمل الواجهة تحديث
  // السياق، لذا نقرأ الجلسة من المصدر قبل أي فحص صلاحية.
  await core.refresh();
  const userId = requireUser(core);
  const requestId = input.requestId.trim();
  if (requestId.length < 8 || requestId.length > 120) {
    throw AppError.validation('معرّف الطلب غير صالح');
  }

  const requests = await core.rawAll('registration_requests');
  const previous = requests.find(
    (request) => request.userId === userId && request.requestId === requestId,
  );
  if (previous?.status === 'completed' && previous.companyId) {
    const company = await core.rawGet('companies', previous.companyId);
    if (company) return company;
  }
  if (previous?.status === 'processing') {
    throw AppError.conflict('طلب التسجيل قيد المعالجة. انتظر لحظات ثم أعد المحاولة.');
  }

  const settings = await readPlatformOperationsSettings(core);
  if (!settings.selfRegistrationEnabled) {
    throw AppError.forbidden('التسجيل الذاتي للمنشآت معطّل من إدارة المنصة');
  }
  const name = requireText(input.name, 'اسم المنشأة', 2, 120);
  const slug = input.slug.trim().toLowerCase();
  if (!SLUG_PATTERN.test(slug)) {
    throw AppError.validation('معرّف المنشأة يجب أن يكون أحرفًا إنجليزية صغيرة وأرقامًا وشرطات');
  }
  const currency = input.currency.trim().toUpperCase();
  const companies = await core.rawAll('companies');
  if (companies.some((company) => company.slug.toLowerCase() === slug)) {
    throw AppError.conflict('معرّف المنشأة مستخدم مسبقًا');
  }

  const nowDate = core.now();
  const now = nowDate.toISOString();
  const company: Company = {
    id: newId(),
    name,
    slug,
    status: 'active',
    timeZone: input.timeZone,
    locale: input.locale || 'ar',
    currency: currency || null,
    phone: null,
    logoAttachmentId: null,
    settings: {},
    limits: { maxBrands: null, maxBranches: null, maxMembers: null, maxStorageBytes: null },
    createdBy: userId,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  };
  await core.rawPut('companies', company);
  await seedCompanyStructure(core, { company, ownerUserId: userId, now });

  if (settings.subscriptionsEnabled && settings.defaultPlanId) {
    const plan = await core.rawGet('subscription_plans', settings.defaultPlanId);
    if (plan?.isActive) {
      const trialDays = plan.trialDays || settings.defaultTrialDays || 0;
      const status: SubscriptionStatus = trialDays > 0 ? 'trialing' : 'pending';
      const subscription: CompanySubscription = {
        id: newId(),
        companyId: company.id,
        planId: plan.id,
        status,
        startsAt: now,
        endsAt: null,
        trialEndsAt:
          trialDays > 0 ? new Date(nowDate.getTime() + trialDays * 86_400_000).toISOString() : null,
        cancelAtPeriodEnd: false,
        source: 'self_service',
        notes: 'اشتراك تجريبي أُنشئ عند التسجيل الذاتي',
        createdBy: userId,
        createdAt: now,
        updatedAt: now,
      };
      await core.rawPut('company_subscriptions', subscription);
      await core.rawPut('subscription_events', {
        id: newId(),
        subscriptionId: subscription.id,
        companyId: company.id,
        action: 'created',
        fromStatus: null,
        toStatus: status,
        metadata: { planId: plan.id, source: 'self_service' },
        actorUserId: userId,
        createdAt: now,
      } satisfies SubscriptionEvent);
    }
  }

  const request: RegistrationRequest = {
    id: newId(),
    userId,
    requestId,
    companyId: company.id,
    status: 'completed',
    createdAt: now,
    updatedAt: now,
  };
  await core.rawPut('registration_requests', request);
  await core.audit({
    companyId: company.id,
    action: 'company.self_registered',
    entityType: 'company',
    entityId: company.id,
    after: { name: company.name, slug: company.slug, requestId },
  });
  await core.refresh();
  return company;
}
